import { Button, Icon, InputGroup, Intent } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import classNames from "classnames";
import * as React from "react";
import styled from "styled-components";

import { useDatatable2Context } from "../DataTable2";
import { useDebouncedCallback } from "nsitools-react";

export interface IFilterInputProps {}

export const CustomInputGroup = styled(InputGroup)`
  margin-bottom: 1em;
  border: none;
  flex: 1;
`;
export const FilterInput: React.FunctionComponent<IFilterInputProps> = () => {
  const {
    tableState: { globalFilter, setGlobalFilter },
    onInputEnter,
    translateFunc,
    filterMode = "OnEnter",
    filterPlaceholder,
    focusOnFilter,
    largeFilter,
    highlightFilter,
    filterDebounceInMs = 500,
  } = useDatatable2Context();

  const debouncedSetGlobalFilter = useDebouncedCallback((filter: string) => {
    setGlobalFilter(filter);
  }, filterDebounceInMs);

  const [currentFilterValue, setCurrentFilterValue] =
    React.useState(globalFilter);

  const filterInputRef = React.useRef<HTMLInputElement>();

  React.useEffect(() => {
    if (focusOnFilter && filterInputRef.current) {
      filterInputRef.current?.focus();
    }
  }, [focusOnFilter]);

  const filterRightElement = React.useMemo(() => {
    return (
      <>
        {currentFilterValue !== null && currentFilterValue !== "" && (
          <Button
            icon={IconNames.ERASER}
            intent={Intent.DANGER}
            minimal={true}
            onClick={() => {
              setCurrentFilterValue("");
              setGlobalFilter("");
            }}
          />
        )}
        {filterMode !== "OnChange" ? (
          <Button
            icon="search"
            intent={Intent.PRIMARY}
            minimal={true}
            disabled={globalFilter === currentFilterValue}
            onClick={() => setGlobalFilter(currentFilterValue)}
            small={true}
          />
        ) : null}
      </>
    );
  }, [currentFilterValue, filterMode, globalFilter, setGlobalFilter]);

  return (
    <CustomInputGroup
      large={largeFilter}
      className={classNames(
        { highlighted: highlightFilter },
        "datatable-filter-input"
      )}
      leftIcon={
        <Icon
          icon={IconNames.FILTER}
          intent={highlightFilter ? Intent.PRIMARY : Intent.NONE}
        />
      }
      rightElement={filterRightElement}
      value={currentFilterValue}
      inputRef={(r) => {
        filterInputRef.current = r;
      }}
      placeholder={filterPlaceholder ?? translateFunc("Filter")}
      onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
        if (filterMode === "OnBlurOrEnter" || filterMode === "OnEnter") {
          if (e.key === "Enter") {
            setGlobalFilter(e.currentTarget.value);
          }
        }
        if (e.key === "Enter" && onInputEnter) {
          onInputEnter(e.currentTarget.value);
        }
      }}
      onChange={(e: any) => {
        setCurrentFilterValue(e.target.value);
        if (filterMode === "OnChange") {
          debouncedSetGlobalFilter(e.target.value);
        }
      }}
      onBlur={(e: any) => {
        if (filterMode === "OnBlurOrEnter") {
          setGlobalFilter(e.target.value);
        }
      }}
    />
  );
};
